import React, { useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

/**
 * Christmas: a string of fairy lights draped along the top edge of the viewport, a sibling
 * of SnowRain (fixed, pointer-transparent, same `paused` contract from the idle manager).
 * One wire and a bulb every ~80px (10 to 24 of them, by viewport width), each twinkling on
 * its own opacity keyframe with a staggered delay. Opacity only, no glow (small glows read
 * as blur), so the whole string costs a couple of dozen nodes and nothing on the main thread.
 */

interface FairyLightsProps {
  paused?: boolean;
}

interface BulbProps {
  $left: number;
  $color: string;
  $duration: number;
  $delay: number;
  $paused: boolean;
  $twinkle: boolean;
}

const BULB_SPACING = 80;
const MIN_BULBS = 10;
const MAX_BULBS = 24;
// Candle amber, berry, holly, frost — the christmas palette's accents, in that order.
const BULB_COLORS = ['#ffb84d', '#e0393e', '#4ade80', '#bfe0ff'];
const WIRE_COLOR = 'rgba(238, 243, 248, 0.32)';

const twinkle = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const String = styled.div.attrs({ className: 'fairy-lights' })`
  position: fixed;
  top: var(--safe-top, 0px);
  left: 0;
  right: 0;
  height: 26px;
  pointer-events: none;
  overflow: hidden;
  z-index: 10000;
`;

const Wire = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 12px;
  fill: none;
  stroke: ${WIRE_COLOR};
  stroke-width: 1.2;
`;

const Bulb = styled.span<BulbProps>`
  position: absolute;
  top: 9px;
  left: ${(props) => props.$left}%;
  width: 8px;
  height: 12px;
  margin-left: -4px;
  border-radius: 50% 50% 50% 50% / 40% 40% 60% 60%;
  background: ${(props) => props.$color};
  /* Under prefers-reduced-motion the string still hangs there, it just doesn't twinkle. */
  animation: ${(props) =>
    props.$twinkle
      ? css`
          ${twinkle} ${props.$duration}s ease-in-out infinite
        `
      : 'none'};
  animation-delay: ${(props) => props.$delay}s;
  animation-play-state: ${(props) => (props.$paused ? 'paused' : 'running')};
  will-change: opacity;

  /* The socket. */
  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: 2px;
    width: 4px;
    height: 4px;
    border-radius: 1px;
    background: ${WIRE_COLOR};
  }
`;

// The wire sags between bulbs. preserveAspectRatio="none" stretches the path to the host
// width; a line is fine to stretch, the bulbs are HTML so they keep their shape.
const buildWire = (count: number): string => {
  const step = 100 / count;
  let d = 'M-2 4';
  for (let i = 0; i <= count; i += 1) {
    const x = i * step;
    const xm = x - step / 2;
    d += i === 0 ? ` L${x.toFixed(2)} 4` : ` Q${xm.toFixed(2)} 11 ${x.toFixed(2)} 4`;
  }
  return `${d} L102 4`;
};

const buildBulbs = (count: number) =>
  Array.from({ length: count }, (_, i) => ({
    id: i,
    left: ((i + 0.5) / count) * 100,
    color: BULB_COLORS[i % BULB_COLORS.length] ?? BULB_COLORS[0] ?? '#ffb84d',
    // Staggered so no two neighbours twinkle in step.
    duration: 2.2 + ((i * 7) % 5) * 0.25,
    delay: -((i * 11) % 13) * 0.3,
  }));

const bulbCountFor = (width: number): number =>
  Math.min(MAX_BULBS, Math.max(MIN_BULBS, Math.round(width / BULB_SPACING)));

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const FairyLights: React.FC<FairyLightsProps> = ({ paused = false }) => {
  const [count, setCount] = useState(() => bulbCountFor(window.innerWidth));
  const twinkling = useMemo(() => !prefersReducedMotion(), []);

  useEffect(() => {
    const handleResize = () => setCount(bulbCountFor(window.innerWidth));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const wirePath = useMemo(() => buildWire(count), [count]);
  const bulbs = useMemo(() => buildBulbs(count), [count]);

  return (
    <String aria-hidden="true">
      <Wire viewBox="0 0 100 12" preserveAspectRatio="none">
        <path d={wirePath} vectorEffect="non-scaling-stroke" />
      </Wire>
      {bulbs.map((bulb) => (
        <Bulb
          key={bulb.id}
          $left={bulb.left}
          $color={bulb.color}
          $duration={bulb.duration}
          $delay={bulb.delay}
          $paused={paused}
          $twinkle={twinkling}
        />
      ))}
    </String>
  );
};

export default FairyLights;
